import { useRef, useState, useCallback } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Play, ArrowDown } from "lucide-react";
import { featured } from "@/data/content";
import { scrollTo } from "@/components/Nav";

const Line = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-[0.12em]">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Hex = ({ className }) => (
  <svg viewBox="0 0 100 100" className={className} fill="none" aria-hidden>
    <polygon
      points="50 3 93.3 26.5 93.3 73.5 50 97 6.7 73.5 6.7 26.5"
      stroke="currentColor"
      strokeWidth="1.2"
    />
  </svg>
);

export const HexEmblem = ({ className = "" }) => (
  <motion.svg
    viewBox="0 0 200 200"
    className={className}
    fill="none"
    aria-hidden
    animate={{ rotate: 360 }}
    transition={{ repeat: Infinity, duration: 90, ease: "linear" }}
  >
    <defs>
      <linearGradient id="hexgrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#00F5D4" />
        <stop offset="50%" stopColor="#38BDF8" />
        <stop offset="100%" stopColor="#2563EB" />
      </linearGradient>
    </defs>
    <polygon points="100 6 181.5 51 181.5 149 100 194 18.5 149 18.5 51" stroke="url(#hexgrad)" strokeWidth="2" />
    <polygon points="100 30 162.2 64.4 162.2 135.6 100 170 37.8 135.6 37.8 64.4" stroke="url(#hexgrad)" strokeWidth="0.8" opacity="0.5" />
  </motion.svg>
);

const VideoCard = ({ v, i, onPlay }) => {
  const vidRef = useRef(null);
  const enter = () => vidRef.current?.play().catch(() => {});
  const leave = () => {
    if (vidRef.current) {
      vidRef.current.pause();
      vidRef.current.currentTime = 0;
    }
  };
  return (
    <motion.button
      data-testid={`hero-featured-video-play-${v.id}`}
      onClick={() => onPlay(v)}
      onMouseEnter={enter}
      onMouseLeave={leave}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay: i * 0.15, ease: [0.16, 1, 0.3, 1] }}
      whileHover={{ y: -8 }}
      className={`group relative block w-full ${v.span} text-left`}
    >
      <div
        className="relative w-full overflow-hidden rounded-xl border border-white/10 bg-elevated transition-shadow duration-500 group-hover:border-turq/50 group-hover:shadow-[0_0_70px_-12px_rgba(6,182,212,0.55)]"
        style={{ aspectRatio: v.ratio }}
      >
        <video
          ref={vidRef}
          src={v.videoUrl}
          poster={v.thumb}
          muted
          loop
          playsInline
          preload="metadata"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/20 to-transparent" />
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-glow/90 text-ink opacity-90 transition-all duration-300 group-hover:scale-110 group-hover:opacity-0">
            <Play size={20} fill="currentColor" />
          </span>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <p className="text-[11px] uppercase tracking-[0.25em] text-turq">{v.tag}</p>
          <p className="mt-1 font-syne text-sm sm:text-base font-semibold text-slate-100">{v.title}</p>
        </div>
      </div>
    </motion.button>
  );
};

export default function Hero({ onPlay }) {
  const { scrollY } = useScroll();
  const ySlow = useTransform(scrollY, [0, 900], [0, 180]);
  const yFast = useTransform(scrollY, [0, 900], [0, -140]);
  const fade = useTransform(scrollY, [0, 600], [1, 0]);
  const [glow, setGlow] = useState({ x: 50, y: 30 });

  const onMove = useCallback((e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setGlow({
      x: ((e.clientX - r.left) / r.width) * 100,
      y: ((e.clientY - r.top) / r.height) * 100,
    });
  }, []);

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative overflow-hidden"
      onMouseMove={onMove}
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at 50% 25%, rgba(6,182,212,0.16) 0%, rgba(10,17,40,0.75) 55%, rgba(11,15,23,1) 100%)",
        }}
      />
      <div
        className="pointer-events-none absolute inset-0 transition-[background] duration-300"
        style={{
          background: `radial-gradient(circle 420px at ${glow.x}% ${glow.y}%, rgba(0,245,212,0.10) 0%, transparent 70%)`,
        }}
      />
      <motion.div style={{ y: ySlow }} className="pointer-events-none absolute inset-0 text-turq/15">
        <Hex className="absolute left-[6%] top-[18%] w-40 sm:w-64 hex-pulse" />
        <Hex className="absolute right-[8%] top-[12%] w-24 sm:w-40 text-eblue/20" />
      </motion.div>
      <motion.div style={{ y: yFast }} className="pointer-events-none absolute inset-0 text-glow/10">
        <Hex className="absolute left-[16%] bottom-[30%] w-20 sm:w-28" />
        <Hex className="absolute right-[16%] bottom-[38%] w-32 sm:w-52 hex-pulse text-amberx/15" />
      </motion.div>

      <HexEmblem className="pointer-events-none absolute -right-24 top-24 w-[380px] h-[380px] sm:w-[520px] sm:h-[520px] opacity-40 hex-glow hidden md:block" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-40 sm:pt-48 pb-16">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="text-xs sm:text-sm uppercase tracking-[0.4em] text-turq mb-8"
        >
          Branded Content Agency — İstanbul
        </motion.p>

        <motion.h1 style={{ opacity: fade }} className="font-syne font-extrabold text-5xl sm:text-6xl lg:text-8xl leading-[1.02] max-w-6xl">
          <Line delay={0.35}>We Integrate <span className="text-gradient">Brand</span></Line>
          <Line delay={0.5}>In the Content</Line>
        </motion.h1>

        <motion.h2 style={{ opacity: fade }} className="mt-8 font-outfit font-light text-base md:text-lg lg:text-2xl text-slate-300 max-w-3xl leading-relaxed">
          <Line delay={0.75}>We Interact with The <span className="text-glow font-normal">Talent</span></Line>
          <Line delay={0.9}>To Extend the Reach of The Brand</Line>
        </motion.h2>

        <motion.button
          data-testid="hero-explore-button"
          onClick={() => scrollTo("about")}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4 }}
          className="mt-12 inline-flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-slate-400 hover:text-glow transition-colors"
        >
          Keşfet
          <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
            <ArrowDown size={16} />
          </motion.span>
        </motion.button>

        <div className="mt-20 sm:mt-24 grid grid-cols-1 lg:grid-cols-12 gap-6 items-end">
          {featured.map((v, i) => (
            <VideoCard key={v.id} v={v} i={i} onPlay={onPlay} />
          ))}
        </div>
      </div>
    </section>
  );
}
